import { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Download,
  Smartphone,
  Mic,
  BookOpen,
  Heart,
  Shield,
  Sparkles,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  QrCode,
  ExternalLink,
  Apple,
  AlertTriangle,
  Bot,
  Workflow,
  Brain,
  GitBranch,
  Zap,
  Camera,
  Music,
  Star,
  Users,
  Gift,
  Image,
  Video,
  Lock,
  Cloud,
  Globe,
} from 'lucide-react'
import { APP_CONFIGS, type IconKey } from '../config/appDownloadConfigs'

interface AppVersion {
  version_name: string
  version_code: number
  platform: string
  file_size: number
  released_at: string
  download_url?: string
  /** Recorded by the AppStore from the stored file; empty on versions uploaded before 2026-10-16. */
  sha256?: string
}

interface AppDetails {
  app_id: string
  name: string
  versions: AppVersion[]
}

type Platform = 'android' | 'ios' | 'windows' | 'web'

const ICON_MAP: Record<IconKey, React.ReactNode> = {
  'mic': <Mic className="w-6 h-6" />,
  'book-open': <BookOpen className="w-6 h-6" />,
  'heart': <Heart className="w-6 h-6" />,
  'shield': <Shield className="w-6 h-6" />,
  'sparkles': <Sparkles className="w-6 h-6" />,
  'bot': <Bot className="w-6 h-6" />,
  'workflow': <Workflow className="w-6 h-6" />,
  'brain': <Brain className="w-6 h-6" />,
  'git-branch': <GitBranch className="w-6 h-6" />,
  'zap': <Zap className="w-6 h-6" />,
  'camera': <Camera className="w-6 h-6" />,
  'music': <Music className="w-6 h-6" />,
  'star': <Star className="w-6 h-6" />,
  'users': <Users className="w-6 h-6" />,
  'gift': <Gift className="w-6 h-6" />,
  'rings': <Heart className="w-6 h-6" />,
  'image': <Image className="w-6 h-6" />,
  'video': <Video className="w-6 h-6" />,
  'lock': <Lock className="w-6 h-6" />,
  'cloud': <Cloud className="w-6 h-6" />,
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/Win64|WOW64|Windows NT/i.test(ua)) return 'windows'
  return 'android'
}

export default function AppDownload() {
  const { appId: paramAppId } = useParams<{ appId: string }>()
  const location = useLocation()

  // For shorthand routes like /mmb, /wedlegacy — extract from path
  const appId = paramAppId || location.pathname.split('/').filter(Boolean).pop() || ''
  const config = APP_CONFIGS[appId]

  const [showInstructions, setShowInstructions] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('android')

  useEffect(() => {
    setSelectedPlatform(detectPlatform())
  }, [])

  const { data: app } = useQuery<AppDetails>({
    queryKey: ['app', appId],
    queryFn: async () => {
      const res = await fetch(`/api/apps/${appId}`)
      return res.json()
    },
    enabled: !!appId,
  })

  // Sort by released_at descending (newest first) — version_code can be inconsistent
  // across uploads (e.g. timestamp-based codes vs sequential), so release date is authoritative.
  const latestAndroid = app?.versions
    ?.filter((v) => v.platform === 'android')
    ?.sort((a, b) => new Date(b.released_at).getTime() - new Date(a.released_at).getTime())[0]

  const latestIOS = app?.versions
    ?.filter((v) => v.platform === 'ios')
    ?.sort((a, b) => new Date(b.released_at).getTime() - new Date(a.released_at).getTime())[0]

  const latestWindows = app?.versions
    ?.filter((v) => v.platform === 'windows')
    ?.sort((a, b) => new Date(b.released_at).getTime() - new Date(a.released_at).getTime())[0]

  const androidVersion = latestAndroid?.version_name || config?.defaultVersion || '1.0.0'
  const androidFileSize = latestAndroid?.file_size
    ? `${(latestAndroid.file_size / 1024 / 1024).toFixed(0)} MB`
    : config?.defaultFileSize || 'N/A'

  const iosVersion = latestIOS?.version_name
  const iosFileSize = latestIOS?.file_size
    ? `${(latestIOS.file_size / 1024 / 1024).toFixed(0)} MB`
    : null

  const windowsVersion = latestWindows?.version_name || (config?.windowsEnabled ? config?.defaultVersion : undefined)
  const windowsFileSize = latestWindows?.file_size
    ? `${(latestWindows.file_size / 1024 / 1024).toFixed(0)} MB`
    : null
  const windowsDownloadUrl = latestWindows?.download_url
    || `/downloads/mskai-apps/windows/${appId}/v${config?.defaultVersion || '0.1.0'}-1.exe`

  const appVersion = selectedPlatform === 'ios' && iosVersion
    ? iosVersion
    : selectedPlatform === 'windows' && windowsVersion
    ? windowsVersion
    : androidVersion
  const appName = config?.name || app?.name || appId

  // Use stored download_url from API (already /downloads/ prefix), or construct it.
  // NOTE: must use /downloads/ (with 's') — nginx/Traefik only proxy that path to MinIO.
  const downloadUrl = latestAndroid?.download_url
    || (latestAndroid
      ? `/downloads/mskai-apps/android/${appId}/v${latestAndroid.version_name}-${latestAndroid.version_code}.apk`
      : config?.downloadPath
        ? `${config.downloadPath}/v${config.defaultVersion}-${config.defaultVersionCode ?? 1}.apk`
        : `/downloads/mskai-apps/android/${appId}/app-release.apk`)
  const iosManifestUrl = config?.iosManifestPath || `/api/manifest/${appId}/ios`

  const handleAndroidDownload = () => {
    setDownloading(true)
    window.location.href = downloadUrl
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleIOSDownload = () => {
    setDownloading(true)
    const manifestFullUrl = `${window.location.origin}${iosManifestUrl}`
    window.location.href = `itms-services://?action=download-manifest&url=${encodeURIComponent(manifestFullUrl)}`
    setTimeout(() => setDownloading(false), 3000)
  }

  const handleWindowsDownload = () => {
    setDownloading(true)
    window.location.href = windowsDownloadUrl
    setTimeout(() => setDownloading(false), 3000)
  }

  // Fallback for unconfigured apps
  if (!config) {
    return (
      <div className="min-h-screen py-16 px-4 text-center">
        <h1 className="text-4xl font-bold mb-4">{appName}</h1>
        <p className="text-gray-400 mb-8">Download page for this app is not yet configured.</p>
        <a
          href={downloadUrl}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg"
        >
          <Download className="w-6 h-6" />
          Download APK
        </a>
      </div>
    )
  }

  const t = config.theme
  // Prefer the AppStore's own recorded digest; fall back to the owner's listing only for the version it describes.
  const sha256ToShow =
    latestAndroid?.sha256 || (config.sha256 && androidVersion === config.listingVersion ? config.sha256 : undefined)

  const androidInstallSteps: { step: number; title: string; description: string }[] = config.installSteps
    ? config.installSteps.map((description, i) => ({ step: i + 1, title: '', description }))
    : [
    { step: 1, title: 'Download the APK', description: 'Click the download button above. Your browser may ask you to confirm the download.' },
    { step: 2, title: 'Enable Unknown Sources', description: 'Go to Settings > Security > Install unknown apps and enable it for your browser or file manager.' },
    { step: 3, title: 'Open the APK File', description: 'Find the downloaded file in your Downloads folder and tap to open it.' },
    { step: 4, title: 'Install the App', description: 'Tap "Install" when prompted. The installation will complete in a few seconds.' },
    { step: 5, title: 'Launch & Sign In', description: config.androidFinalStep },
  ]

  const iosInstallSteps = [
    { step: 1, title: 'Device Registration Required', description: 'Your iPhone/iPad must be registered with our Apple Developer account. Contact us if you haven\'t done this.' },
    { step: 2, title: 'Tap "Install on iOS"', description: 'Click the install button above. Safari will prompt you to install the app.' },
    { step: 3, title: 'Trust the Developer', description: 'Go to Settings > General > VPN & Device Management, find our profile and tap "Trust".' },
    { step: 4, title: 'Launch the App', description: config.iosFinalStep },
  ]

  const windowsInstallSteps = [
    { step: 1, title: 'Download the Installer', description: 'Click the Download for Windows button above. The NSIS installer (.exe) will be saved to your Downloads folder.' },
    { step: 2, title: 'Run the Installer', description: 'Double-click the downloaded .exe. If Windows SmartScreen appears, click "More info" then "Run anyway" — Harvey is a trusted Coherence ecosystem app.' },
    { step: 3, title: 'Follow the Setup Wizard', description: 'Accept the licence, choose your install location (default is your user profile), and click Install. No admin rights required.' },
    { step: 4, title: 'Harvey Starts in the Tray', description: 'After installation Harvey launches automatically and appears as an "H" icon near the system clock. It starts with Windows by default.' },
    { step: 5, title: 'Connect & Bind Identity', description: config?.windowsFinalStep || config?.androidFinalStep || '' },
  ]

  const webInstallSteps = [
    { step: 1, title: 'Open the Web App', description: 'Click "Open Web App" above — it opens directly in your browser, no download or install required.' },
    { step: 2, title: 'Add to Home Screen (optional)', description: 'For an app-like experience with an icon and offline support, use your browser\'s "Add to Home Screen" (Android Chrome, iOS Safari) or "Install App" (desktop Chrome/Edge) option.' },
    { step: 3, title: 'Launch & Sign In', description: config?.webFinalStep || '' },
  ]

  const installSteps = selectedPlatform === 'ios'
    ? iosInstallSteps
    : selectedPlatform === 'windows'
    ? windowsInstallSteps
    : selectedPlatform === 'web'
    ? webInstallSteps
    : androidInstallSteps

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 px-4">
        <div className={`absolute inset-0 ${t.heroBg}`} />

        <div className="relative max-w-4xl mx-auto text-center">
          {/* App Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <img
                src={config.iconPath}
                alt={config.name}
                className={`w-32 h-32 rounded-3xl shadow-2xl ${t.iconShadow}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const wrapper = target.parentElement!
                  const fallback = document.createElement('div')
                  fallback.className = `w-32 h-32 rounded-3xl shadow-2xl bg-gradient-to-br ${t.iconFallbackGradient} flex items-center justify-center`
                  fallback.innerHTML = `<span class="text-5xl font-bold text-white">${config.name[0]}</span>`
                  const badge = document.createElement('div')
                  badge.className = 'absolute -bottom-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full'
                  badge.textContent = `v${appVersion}`
                  wrapper.appendChild(fallback)
                  wrapper.appendChild(badge)
                }}
              />
              <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                v{appVersion}
              </div>
            </div>
          </div>

          {/* Title & Description */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            <span className={t.titleGradient}>{config.name}</span>
          </h1>

          <p className="text-sm md:text-lg lg:text-xl text-gray-300 mb-2">{config.tagline}</p>
          <p className="text-xs md:text-sm lg:text-base text-gray-400 max-w-2xl mx-auto mb-6 md:mb-8">{config.description}</p>
          {config.audience && (
            <p className="text-xs md:text-sm text-gray-500 max-w-2xl mx-auto mb-6 md:mb-8">{config.audience}</p>
          )}

          {/* Platform Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-4 md:mb-6">
            <button
              type="button"
              data-testid="platform-android"
              onClick={() => setSelectedPlatform('android')}
              className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] md:min-h-auto text-sm md:text-base ${
                selectedPlatform === 'android'
                  ? 'bg-green-500/20 border-2 border-green-500 text-green-400'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
              }`}
            >
              <Smartphone className="w-5 h-5 flex-shrink-0" />
              Android
            </button>
            <button
              type="button"
              data-testid="platform-ios"
              onClick={() => setSelectedPlatform('ios')}
              className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] md:min-h-auto text-sm md:text-base ${
                selectedPlatform === 'ios'
                  ? 'bg-gray-500/20 border-2 border-gray-400 text-gray-200'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
              }`}
            >
              <Apple className="w-5 h-5 flex-shrink-0" />
              iOS
            </button>
            {(latestWindows || config?.windowsEnabled) && (
              <button
                type="button"
                data-testid="platform-windows"
                onClick={() => setSelectedPlatform('windows')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] md:min-h-auto text-sm md:text-base ${
                  selectedPlatform === 'windows'
                    ? 'bg-blue-500/20 border-2 border-blue-400 text-blue-200'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Download className="w-5 h-5 flex-shrink-0" />
                Windows
              </button>
            )}
            {config?.webEnabled && config?.webUrl && (
              <button
                type="button"
                data-testid="platform-web"
                onClick={() => setSelectedPlatform('web')}
                className={`px-4 md:px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all min-h-[44px] md:min-h-auto text-sm md:text-base ${
                  selectedPlatform === 'web'
                    ? 'bg-cyan-500/20 border-2 border-cyan-400 text-cyan-200'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
                }`}
              >
                <Globe className="w-5 h-5 flex-shrink-0" />
                Web
              </button>
            )}
          </div>

          {/* Download Button — Android */}
          {selectedPlatform === 'android' && (
            <div className="flex flex-col items-center gap-4 mb-8">
              <button
                type="button"
                onClick={handleAndroidDownload}
                disabled={downloading}
                className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-500/30 hover:shadow-green-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
              >
                <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                {downloading ? 'Starting Download...' : 'Download for Android'}
                <Smartphone className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Version {androidVersion}</span>
                <span>&bull;</span>
                <span>{androidFileSize}</span>
                <span>&bull;</span>
                <span>{config.platformNote ?? 'Android 7.0+'}</span>
              </div>
            </div>
          )}

          {/* Download Button — iOS */}
          {selectedPlatform === 'ios' && (
            <div className="flex flex-col items-center gap-4 mb-8">
              {iosVersion ? (
                <>
                  <button
                    type="button"
                    onClick={handleIOSDownload}
                    disabled={downloading}
                    className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600 hover:from-gray-500 hover:via-gray-400 hover:to-gray-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gray-500/30 hover:shadow-gray-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
                  >
                    <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                    {downloading ? 'Installing...' : 'Install on iOS'}
                    <Apple className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span>Version {iosVersion}</span>
                    {iosFileSize && (<><span>&bull;</span><span>{iosFileSize}</span></>)}
                    <span>&bull;</span>
                    <span>iOS 14.0+</span>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm max-w-md">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-amber-200">
                        Your device must be registered with our developer account to install this app.
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md">
                    <Apple className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">{config.iosStatement ? 'iOS' : 'iOS Coming Soon'}</h3>
                    <p className="text-gray-400 text-sm">
                      {config.iosStatement ??
                        'The iOS version is currently being prepared. Please check back soon or download the Android version.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Download Button — Windows */}
          {selectedPlatform === 'windows' && (
            <div className="flex flex-col items-center gap-4 mb-8">
              <button
                type="button"
                onClick={handleWindowsDownload}
                disabled={downloading}
                className={`group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center gap-3 ${downloading ? 'opacity-75 cursor-wait' : ''}`}
              >
                <Download className={`w-6 h-6 ${downloading ? 'animate-bounce' : 'group-hover:animate-bounce'}`} />
                {downloading ? 'Downloading...' : 'Download for Windows'}
                <ExternalLink className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Version {windowsVersion}</span>
                {windowsFileSize && (<><span>&bull;</span><span>{windowsFileSize}</span></>)}
                <span>&bull;</span>
                <span>Windows 10/11 x64</span>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm max-w-md">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-200">
                    NSIS installer. Run as administrator if prompted. Harvey installs to your user profile and starts automatically with Windows.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Launch — Web (PWA), no download needed */}
          {selectedPlatform === 'web' && config?.webUrl && (
            <div className="flex flex-col items-center gap-4 mb-8">
              <a
                href={config.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative px-8 py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-500 hover:from-cyan-600 hover:via-sky-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 flex items-center gap-3"
              >
                <Globe className="w-6 h-6 group-hover:animate-pulse" />
                Open Web App
                <ExternalLink className="w-5 h-5" />
              </a>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>No install required</span>
                <span>&bull;</span>
                <span>Works in any modern browser</span>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-sm max-w-md">
                <div className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-cyan-200">{config.webFinalStep}</span>
                </div>
              </div>
            </div>
          )}

          {/* QR Code — Android */}
          {selectedPlatform === 'android' && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-sm mx-auto mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <QrCode className={`w-5 h-5 ${t.accentText}`} />
                <span className="font-medium">Scan to Download</span>
              </div>
              <div className="bg-white p-4 rounded-xl inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://appstore.multiskilled.ai${downloadUrl}`)}`}
                  alt="QR Code for Android"
                  className="w-36 h-36"
                />
              </div>
              <p className="text-xs text-gray-500 mt-3">Scan with your Android phone's camera</p>
            </div>
          )}

          {/* QR Code — iOS */}
          {selectedPlatform === 'ios' && iosVersion && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-sm mx-auto mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <QrCode className="w-5 h-5 text-gray-400" />
                <span className="font-medium">Scan to Install</span>
              </div>
              <div className="bg-white p-4 rounded-xl inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://appstore.multiskilled.ai/download/${appId}`)}`}
                  alt="QR Code for iOS"
                  className="w-36 h-36"
                />
              </div>
              <p className="text-xs text-gray-500 mt-3">Scan with your iPhone camera, then tap "Install"</p>
            </div>
          )}

          {/* QR Code — Web */}
          {selectedPlatform === 'web' && config?.webUrl && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-sm mx-auto mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <QrCode className="w-5 h-5 text-cyan-400" />
                <span className="font-medium">Scan to Open</span>
              </div>
              <div className="bg-white p-4 rounded-xl inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(config.webUrl)}`}
                  alt="QR Code for Web App"
                  className="w-36 h-36"
                />
              </div>
              <p className="text-xs text-gray-500 mt-3">Scan with any phone camera to open in your browser</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 md:py-16 px-4 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12">
            {config.featuresHeading ? (
              config.featuresHeading
            ) : (
              <>
                Why People Love <span className={t.accentText}>{config.name}</span>
              </>
            )}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {config.features.map((feature, idx) => (
              <div
                key={idx}
                className={`bg-white/5 border border-white/10 rounded-xl p-4 md:p-6 ${t.featureBorder} transition-colors`}
              >
                <div className={`${t.accentText} mb-3 md:mb-4`}>{ICON_MAP[feature.iconKey]}</div>
                <h3 className="font-semibold text-base md:text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-xs md:text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots (owner-supplied, with the owner's captions) */}
      {config.screenshots && config.screenshots.length > 0 && (
        <section className="py-8 md:py-12 px-4 border-t border-white/10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-6">Screenshots</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {config.screenshots.map((shot) => (
                <figure key={shot.src} className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <img src={shot.src} alt={shot.caption} loading="lazy" className="rounded-lg w-full" />
                  <figcaption className="text-xs text-gray-400 mt-2">{shot.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Known limits (owner-supplied; shown in full, never trimmed) */}
      {config.knownLimits && config.knownLimits.length > 0 && (
        <section className="py-8 md:py-12 px-4 border-t border-white/10" data-testid="known-limits">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-6">Known limits</h2>
            <ul className="space-y-3 text-sm text-gray-300 list-disc pl-5">
              {config.knownLimits.map((limit, idx) => (
                <li key={idx}>{limit}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Installation Instructions */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full bg-white/5 border border-white/10 rounded-xl p-6 flex items-center justify-between hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center gap-3">
              {selectedPlatform === 'ios' ? (
                <Apple className="w-6 h-6 text-gray-400" />
              ) : selectedPlatform === 'windows' ? (
                <Download className="w-6 h-6 text-blue-400" />
              ) : selectedPlatform === 'web' ? (
                <Globe className="w-6 h-6 text-cyan-400" />
              ) : (
                <Smartphone className="w-6 h-6 text-green-400" />
              )}
              <span className="font-semibold text-lg">
                {selectedPlatform === 'ios' ? 'iOS' : selectedPlatform === 'windows' ? 'Windows' : selectedPlatform === 'web' ? 'Web' : 'Android'} {selectedPlatform === 'web' ? 'Access' : 'Installation'} Guide
              </span>
            </div>
            {showInstructions ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showInstructions && (
            <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-6 space-y-6">
              {selectedPlatform === 'android' ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                  <p className="text-yellow-200">
                    <strong>Note:</strong> Since this app is distributed outside the Play Store,
                    you'll need to enable installation from unknown sources. {config.unknownSourceNote}
                  </p>
                </div>
              ) : selectedPlatform === 'windows' ? (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
                  <p className="text-blue-200">
                    <strong>Note:</strong> Windows may show a SmartScreen warning because Harvey is distributed outside the Microsoft Store.
                    Click "More info" → "Run anyway" to proceed. {config.unknownSourceNote}
                  </p>
                </div>
              ) : selectedPlatform === 'web' ? (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 text-sm">
                  <p className="text-cyan-200">
                    <strong>Note:</strong> No install, no unknown-sources warning, no device registration — it's just a webpage.
                    Some features (background sync, local model access) work best on desktop/phone, but the core queue is fully usable here.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
                  <p className="text-blue-200">
                    <strong>Note:</strong> iOS apps distributed outside the App Store require your device's UDID to be registered
                    with our Apple Developer account. Contact us to register your device.
                  </p>
                </div>
              )}

              {installSteps.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className={`flex-shrink-0 w-8 h-8 ${t.stepBg} rounded-full flex items-center justify-center text-sm font-bold`}>
                    {item.step}
                  </div>
                  <div>
                    {item.title && <h4 className="font-medium mb-1">{item.title}</h4>}
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}

              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-green-200 font-medium mb-1">You're all set!</p>
                  <p className="text-green-300/70">{config.completionMessage}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-8 text-center">
          {config.trustBadges.map((badge, idx) => (
            <div key={idx}>
              <div className={`text-3xl font-bold ${t.accentText}`}>{badge.value}</div>
              <div className="text-sm text-gray-500">{badge.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Direct Link Footer */}
      <section className="py-8 px-4 border-t border-white/10 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm mb-2">{selectedPlatform === 'web' ? 'Direct link:' : 'Direct download link:'}</p>
          {selectedPlatform === 'android' ? (
            <a
              href={downloadUrl}
              className={`${t.accentText} hover:opacity-80 text-sm flex items-center justify-center gap-1`}
            >
              appstore.multiskilled.ai{downloadUrl}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : selectedPlatform === 'windows' ? (
            <a
              href={windowsDownloadUrl}
              className={`${t.accentText} hover:opacity-80 text-sm flex items-center justify-center gap-1`}
            >
              appstore.multiskilled.ai{windowsDownloadUrl}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : selectedPlatform === 'web' && config?.webUrl ? (
            <a
              href={config.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${t.accentText} hover:opacity-80 text-sm flex items-center justify-center gap-1`}
            >
              {config.webUrl.replace(/^https?:\/\//, '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : iosVersion ? (
            <span className="text-gray-400 text-sm">
              iOS installation available via the Install button above (requires registered device)
            </span>
          ) : (
            <span className="text-gray-500 text-sm">{config.iosStatement ?? 'iOS version coming soon'}</span>
          )}
          {selectedPlatform === 'android' && sha256ToShow && (
            <p className="text-gray-500 text-xs mt-3 break-all" data-testid="sha256">
              SHA-256 of {androidVersion}: <span className="font-mono">{sha256ToShow}</span>
            </p>
          )}
          {config.contentSource && <p className="text-gray-600 text-xs mt-3">Page content: {config.contentSource}</p>}
        </div>
      </section>
    </div>
  )
}
